import {Device} from '@fantasmagames/core/src/device/Device';
import {CoreDI} from '@fantasmagames/core/src/di/CoreDI';
import {TextStyles} from '@fantasmagames/core/src/display/texts/TextStyles';
import {AsyncEventManager} from '@fantasmagames/core/src/events/AsyncEventManager';
import {EventManagerWithAsync} from '@fantasmagames/core/src/events/EventManagerWithAsync';
import {IAsyncEventManager} from '@fantasmagames/core/src/events/_interfaces/IAsyncEventManager';
import {IEventManagerWithAsync} from '@fantasmagames/core/src/events/_interfaces/IEventManager';
import {RectangleArea} from '@fantasmagames/core/src/geom/RectangleArea';
import {Platform, ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {FullscreenManager} from '@fantasmagames/core/src/screen/fullscreen/FullscreenManager';
import {ResolutionManager} from '@fantasmagames/core/src/screen/ResolutionManager';
import {VisibilityManager} from '@fantasmagames/core/src/screen/VisibilityManager';
import {UpdateManager} from '@fantasmagames/core/src/update/UpdateManager';
import {CurrencyFormatter} from '@fantasmagames/core/src/utils/CurrencyFormatter';
import DI from '@fantasmagames/slot/src/common/inversify/constants/DI';
import {Layout} from '@fantasmagames/slot/src/layout/Layout';
import {Translator} from '@fantasmagames/slot/src/modules/paytable/Translator';
import {StaticStrings} from '@fantasmagames/slot/src/system/messages/StaticStrings';
import {Container, injectable} from 'inversify';
import * as WebFont from 'webfontloader';

type initViewportCallback = (device: Device, viewport: RectangleArea) => void;

export enum WebGlPerformanceCaveatResult {
	SOFTWARE = 0,
	HARDWARE,
	NOT_IMPLEMENTED,
}

@injectable()
export class System {
	protected static MOBILE_HAS_TWO_VIEWS: boolean = false;
	protected _viewport: RectangleArea;
	protected _device: Device;
	protected _urlParams: any;
	protected _resolutionManager: ResolutionManager;
	protected _fullscreenManager: FullscreenManager;
	protected _app: PIXI.Application;
	protected _updateManager: UpdateManager;
	protected _container: Container;
	protected _resourcesLoader: ResourcesLoader;
	protected _currencyFormatter: CurrencyFormatter;

	protected _isTickerPaused: boolean = false;
	protected _isOneFrameExpected: boolean = false;

	protected _majorCaveatPerformance: number;
	protected _textHeadsUpItems: HTMLElement[] = [];
	protected _headsUpParent: HTMLElement;

	constructor(
		initViewport: initViewportCallback,
		onReady: Function,
		getBaseUrlPath: Function = null,
		majorCaveatPerformance: WebGlPerformanceCaveatResult = WebGlPerformanceCaveatResult.HARDWARE,
	) {
		this._majorCaveatPerformance = majorCaveatPerformance;

		this._viewport = new RectangleArea();
		this._urlParams = this.parseUrlParams();

		if (!getBaseUrlPath) {
			getBaseUrlPath = this.baseUrlPath.bind(this);
		}

		this._currencyFormatter = this.createCurrencyFormatter();
		const locale = this.getLocale();
		this.setupReelConstants();

		this._device = new Device(() => {
			const fontPath = getBaseUrlPath('resources/fonts/FStyles.css');
			const fontFamily = this.setFontFamily(locale);

			const currencyISO: string = this.getCurrencyISO();

			const WebFontConfig = {
				custom: {
					families: [fontFamily],
					urls: [fontPath],
				},

				fontloading: () => {
					function generateFontItem(fontFamily: string): void {
						const element = document.createElement('p');
						element.style.fontFamily = fontFamily;
						element.style.fontSize = '0px';
						element.style.visibility = 'hidden';
						element.innerHTML =
							'{"BaseString:c_blΥπόλοι,wΚέρδςΠντάσμαyΝΑΊoΌΧΙkATRÁ%MINGupΤΜΟ ΔΡυθίεκηωήfφqΓγΖΩΕξmΣζd\\ΒΛΆΎώhLΦ.χKPOUCDEvV}WΗΞ2ΉΘ;!1<0>x[ύβψ9]34«»5()-6Έ&78F=+*@$¤ϋ/HJQXYZjz가개갸거게겨고괴괘교구귀궤규그긔기나내냐너네녀노뇌놰뇨누뉘눼뉴느늬니다대댜더데뎌도되돼됴두뒤뒈류드듸디라래랴러레려로뢰뢔료루뤼뤠르리마매먀머메며모뫼뫠묘무뮈뭬뮤므믜미바배뱌버베벼보뵈봬뵤부뷔붸뷰브븨비사새샤서세셔소쇠쇄쇼수쉬쉐슈아애야어에여오외왜요우위웨유으의이자재쟈저제져조죄좨죠주쥐줴쥬즈즤지차채챠처체쳐초최쵀쵸추취췌츄츠치카캐캬커케켜코쾨쾌쿄쿠퀴퀘큐크키타태탸터테텨토퇴퇘툐투튀퉤튜트틔티파패퍄퍼페펴포푀표푸퓌퓨프피하해햐허헤혀호회홰효후휘훼휴흐희히‘?’“”#÷×';
						document.body.appendChild(element);
					}

					generateFontItem(fontFamily);
				},

				active: () => {
					setTimeout(() => {
						this.loadAndStartGame(initViewport, onReady, getBaseUrlPath, locale, currencyISO);
					}, 500);
				},

				inactive: () => {
					setTimeout(() => {
						this.loadAndStartGame(initViewport, onReady, getBaseUrlPath, locale, currencyISO);
					}, 500);
				},
			};

			WebFont.load(WebFontConfig);
		});
	}

	public endLoading(): void {
		this._resourcesLoader.endLoading();
	}

	public setupMobileApproach(hasTwoViews: boolean): void {
		System.MOBILE_HAS_TWO_VIEWS = hasTwoViews;
	}

	public shouldUseMultipleViews(): boolean {
		return this.device.isMobile && System.MOBILE_HAS_TWO_VIEWS;
	}

	protected setupReelConstants(): void {
		throw new Error('Needs to be implemented.');
	}

	protected isSupportedDeviceAndBrowser(device: Device): boolean {
		return true;
	}

	protected showUnsupportedDeviceText(): void {
		// no strings for this.
	}

	protected showUnsupportedBrowserText(): void {
		// no strings for this
	}

	protected showUpdateBrowserText(): void {
		const text = StaticStrings.updateBrowser(this.getLocale());
		this.addHeadsUpTextElement(text, 'update_browser');
	}

	protected showActivateHardwareAccelerationText(): void {
		const text = StaticStrings.hardwareAcceleration(this.getLocale());
		this.addHeadsUpTextElement(text, 'hardware_acceleration');
	}

	protected showOptimizedForChromeText(): void {
		const text = StaticStrings.optimizedForChrome(this.getLocale());
		this.addHeadsUpTextElement(text, 'optimized_for_chrome');
	}

	protected addHeadsUpTextElement(text: string, id: string): HTMLElement {
		const textItem = document.createElement('div');

		textItem.innerText = text;
		textItem.style.paddingBottom = '1.0em';
		textItem.style.fontSize = '1.0em';
		textItem.style.color = 'white';
		textItem.style.position = 'relative';
		textItem.style.webkitTransform = 'translateX(-50%)';
		textItem.style.transform = 'translateX(-50%)';

		textItem.id = id;

		textItem.className = 'FANTASMAHEADSUP';

		this.headsUpParent.appendChild(textItem);

		this._textHeadsUpItems.push(textItem);

		return textItem;
	}

	protected loadAndStartGame(
		initViewport: initViewportCallback,
		onReady: Function,
		getBaseUrlPath: Function,
		locale: string,
		currencyISO: string,
	): void {
		initViewport(this.device, this._viewport);
		this._resourcesLoader = this.createResourceLoader(getBaseUrlPath, locale);

		if (!__PRODUCTION__) {
			(this._resourcesLoader as any)._useSingleSpineAtlas = this._urlParams.useSpineAtlas === 'true';
		}

		if (!this.isSupportedDeviceAndBrowser(this.device)) {
			// show error for unsupported device.
			this.showUpdateBrowserText();
			if (!this.device.chrome) {
				this.showOptimizedForChromeText();
			}
			// this._resourcesLoader.usingUnsupportedDeviceOrBrowser();
			// console.error('Device and browser combination is unsupported. Please upgrade');

			return;
		}

		if (this._majorCaveatPerformance === WebGlPerformanceCaveatResult.SOFTWARE) {
			this.showActivateHardwareAccelerationText();
		}

		if (this._majorCaveatPerformance === WebGlPerformanceCaveatResult.NOT_IMPLEMENTED) {
			this.showUpdateBrowserText();
			if (!this.device.chrome) {
				this.showOptimizedForChromeText();
			}
		}

		this._resourcesLoader.addCurrencyToResourcesList(() => {}, currencyISO);
		this._resourcesLoader.calculateExpectedTotal();

		this._resourcesLoader.loadConfigFiles(() => {
			this._resourcesLoader.loadHelpStyles(() => {
				this._resourcesLoader.loadHelpFiles(() => {
					this._resourcesLoader.loadTranslations(() => {
						this._resourcesLoader.loadLocaleTexts(() => {
							this._resourcesLoader.loadParticles(() => {
								this._resourcesLoader.loadSounds(() => {
									this._resourcesLoader.loadSoundsConfig(() => {
										this._resourcesLoader.loadSingleSpineAtlas(() => {
											this._resourcesLoader.load(
												() => {
													this.onInitialized();
													onReady();
												},
												this._device.isMobile ? Platform.MOBILE : Platform.DESKTOP,
											);
										});
									});
								});
							});
						}, locale);
					}, locale);
				}, locale);
			});
		});
	}

	protected createResourceLoader(getBaseUrlPath: Function, locale: string): ResourcesLoader {
		return new ResourcesLoader(this.device, getBaseUrlPath, locale, this._majorCaveatPerformance);
	}

	protected createCurrencyFormatter(): CurrencyFormatter {
		return new CurrencyFormatter();
	}

	protected getCurrencyISO(): string {
		return 'USD';
	}

	protected baseUrlPath(resource: string): string {
		return '../' + resource;
	}

	protected setFontFamily(locale: string): string {
		let fontFamily = 'Black Han Sans';

		if (locale === 'ja') {
			fontFamily = 'Nicokaku';
		} else if (locale === 'bg' || locale === 'ru' || locale === 'el') {
			fontFamily = 'Alegreya Sans Sc';
		} else if (locale === 'th') {
			fontFamily = 'Pridi Regular';
		}

		return fontFamily;
	}

	protected parseUrlParams(): any {
		const urlParams = {};
		const pl = /\+/g; // regex for replacing addition symbol with a space
		const search = /([^&=]+)=?([^&]*)/g;

		const decode = (s) => {
			return decodeURIComponent(s.replace(pl, ' '));
		};

		const query = window.location.search.substring(1);
		let match;

		while ((match = search.exec(query))) {
			urlParams[decode(match[1])] = decode(match[2]);
		}

		return urlParams;
	}

	protected getLocale(): string {
		return this.urlParams && this.urlParams.locale ? this.urlParams.locale : 'en';
	}

	protected clearHeadsUpItems(): void {
		for (const item in this._textHeadsUpItems) {
			if (this._textHeadsUpItems.hasOwnProperty(item)) {
				const parent = this._textHeadsUpItems[item].parentNode;
				parent.removeChild(this._textHeadsUpItems[item]);
			}
		}

		if (this._headsUpParent) {
			this._headsUpParent.parentNode.removeChild(this._headsUpParent);
		}
	}

	protected onInitialized(): void {
		this.clearHeadsUpItems();

		this._container = new Container();

		this._container.bind<System>(CoreDI.System).toConstantValue(this);
		this._container.bind<ResourcesLoader>(CoreDI.ResourceLoader).toConstantValue(this._resourcesLoader);

		this._container
			.bind<Layout>(DI.Layout)
			.to(Layout)
			.inSingletonScope();

		this._container
			.bind<IAsyncEventManager>(CoreDI.IAsyncEventManager)
			.to(AsyncEventManager)
			.inSingletonScope();

		this._container
			.bind<IEventManagerWithAsync>(CoreDI.IEventManagerWithAsync)
			.to(EventManagerWithAsync)
			.inSingletonScope();

		this._resolutionManager = new ResolutionManager(this._viewport, this._device);
		this._updateManager = new UpdateManager();

		this._container
			.bind<Translator>(DI.Translator)
			.to(Translator)
			.inSingletonScope();

		this._container.get<Translator>(DI.Translator).init(this.getLocale());

		this._container.bind<CurrencyFormatter>(DI.CurrencyFormatter).toConstantValue(this._currencyFormatter);

		this._container
			.bind<TextStyles>(DI.TextStyles)
			.to(TextStyles)
			.inSingletonScope();

		this._container.get<TextStyles>(DI.TextStyles).init(this.getLocale(), this._resolutionManager,
			this._device, this._resourcesLoader.getConfig('config.json'));

		this._app = new PIXI.Application({
			width: this._viewport.width,
			height: this._viewport.height,
			autoResize: true,
			resolution: 1,
			// resolution: this._resolutionManager.defaultViewportResolution.x,

			// webGL makes the screen flickering on v53 Chrome for Android (The issue is occurring only with alpha:false option.)
			// https://bugs.chromium.org/p/chromium/issues/detail?id=651470
			transparent: false, // this was true, probably not needed?
		});

		///////////////////////////////////////////////////////////////////////////////////////
		if (!__PRODUCTION__) {
			document.addEventListener('keydown', this.keyboardInput.bind(this));
			(window as any).tickerMultiplier = 1.0;
			const ticker = document.createElement('div');
			ticker.id = 'TickerComponent';
			ticker.style.top = '50px';
			ticker.style.right = '100px';
			ticker.style.position = 'absolute';
			ticker.style.zIndex = '191919';
			ticker.style.visibility = 'hidden';
			ticker.style.color = 'white';
			document.body.appendChild(ticker);
		}
		///////////////////////////////////////////////////////////////////////////////////////

		this._app.renderer.on('prerender', () => {
			if (!this._isTickerPaused || this._isOneFrameExpected) {
				if (this._isOneFrameExpected) {
					this._isOneFrameExpected = false;
				}

				if (!__PRODUCTION__) {
					this._updateManager.update((this.app.ticker.deltaTime / PIXI.settings.TARGET_FPMS) * (window as any).tickerMultiplier);
				}

				if (__PRODUCTION__) {
					this._updateManager.update(this.app.ticker.deltaTime / PIXI.settings.TARGET_FPMS);
				}
			}
		});

		this._container.bind<Device>(CoreDI.Device).toConstantValue(this._device);
		this._container.bind<UpdateManager>(CoreDI.UpdateManager).toConstantValue(this.updateManager);
		this._container.bind<ResolutionManager>(CoreDI.ResolutionManager).toConstantValue(this._resolutionManager);
		this._container.bind<PIXI.Application>(CoreDI.Application).toConstantValue(this.app);
		this.bindFullscreenManager();
		this._container
			.bind<VisibilityManager>(CoreDI.VisibilityManager)
			.to(VisibilityManager)
			.inSingletonScope();

		this.initAndBindIntegrationSpecificServices();

		this._app.view.id = 'GameCanvas';
		this._app.view.style.position = 'absolute';
		const mainGameDiv = document.getElementById('gameContainer');
		mainGameDiv.appendChild(this._app.view);

		this._fullscreenManager = this._container.get<FullscreenManager>(CoreDI.FullscreenManager);
	}

	protected initAndBindIntegrationSpecificServices(): void {
		//
	}

	protected bindFullscreenManager(): void {
		this._container
			.bind<FullscreenManager>(CoreDI.FullscreenManager)
			.to(FullscreenManager)
			.inSingletonScope();
	}

	protected keyboardInput(event: KeyboardEvent): void {
		if (!__PRODUCTION__) {
			if (event.code === 'ArrowUp') {
				if (event.shiftKey) {
					(window as any).tickerMultiplier += 0.01;
				} else if (event.ctrlKey) {
					(window as any).tickerMultiplier += 10.0;
				} else if (event.altKey) {
					(window as any).tickerMultiplier += 1.0;
				} else {
					(window as any).tickerMultiplier += 0.1;
				}

				document.getElementById('TickerComponent').innerHTML = (window as any).tickerMultiplier;
			}

			if (event.code === 'ArrowDown') {
				if (event.shiftKey) {
					(window as any).tickerMultiplier += 0.01;
				} else if (event.ctrlKey) {
					(window as any).tickerMultiplier -= (window as any).tickerMultiplier > 10 ? 10.0 : 0;
				} else if (event.altKey) {
					(window as any).tickerMultiplier -= (window as any).tickerMultiplier > 1 ? 1.0 : 0;
				} else {
					(window as any).tickerMultiplier -= 0.1;
				}

				document.getElementById('TickerComponent').innerHTML = (window as any).tickerMultiplier;
			}

			if (event.code === 'KeyP') {
				if (event.shiftKey) {
					(window as any).tickerMultiplier = 1;
					document.getElementById('TickerComponent').style.visibility = 'hidden';
				} else if (event.altKey) {
					(window as any).tickerMultiplier = 0.1;
					document.getElementById('TickerComponent').style.visibility = 'visible';
				} else {
					this._isTickerPaused = !this._isTickerPaused;
				}
			}

			if (event.code === 'Period') {
				this._isOneFrameExpected = true;
			}
		}
	}

	protected get headsUpParent(): HTMLElement {
		if (this._headsUpParent) {
			return this._headsUpParent;
		}

		this._headsUpParent = document.createElement('div');

		this._headsUpParent.style.position = 'absolute';
		this._headsUpParent.style.left = '50%';
		this._headsUpParent.style.top = '1em';

		document.body.appendChild(this._headsUpParent);

		return this._headsUpParent;
	}

	public get updateManager(): UpdateManager {
		return this._updateManager;
	}

	public get app(): PIXI.Application {
		return this._app;
	}

	public get device(): Device {
		return this._device;
	}

	public get urlParams(): any {
		return this._urlParams;
	}

	public get resolutionManager(): ResolutionManager {
		return this._resolutionManager;
	}

	public get container(): Container {
		return this._container;
	}

	public get viewport(): RectangleArea {
		return this._viewport;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/System.ts